import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'context/AlertContext';

import { Loader } from '@googlemaps/js-api-loader';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

import { useImageCropper } from 'hooks/useImageCropper';
import ImageCropper from 'components/ImageCropper';
import FloorPlan from 'components/floorplan';

import {
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

import { getEventBookings } from '../../redux/actions/bookings';
import {
  cancelEvent,
  deleteEvent,
  updateEvent,
} from '../../redux/actions/events';
import { createSpaces, updateSpace } from '../../redux/actions/spaces';

import CategorySelectorGrid from 'components/CategorySelectorGrid';
import { categories as allCategories } from 'utils/categories';
import { UTILITIES, getUtilityLabel } from 'utils/utilities';
import normalizeEventDate from 'utils/normalizeEventDate';
import { getEffectiveBookingStatus } from 'utils/bookingActionability';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
  IMAGE_ACCEPT,
  DOCUMENT_ACCEPT,
} from 'utils/uploadValidation';

import { cx, formatEuro } from '../EventManagerModal/parts/atoms';

// ---------- prototype-aligned layout primitives ----------
// Width-less base so narrow controls (the per-space "add units" stepper) can
// set their own — Tailwind resolves w-full over w-20 whatever the class order.
const INPUT_BASE =
  'px-3 py-2 rounded-lg ring-1 ring-gray-200 focus:ring-2 focus:ring-brand outline-none text-sm bg-white transition disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed';
const INPUT_CLS = `w-full ${INPUT_BASE}`;

function Section({ title, subtitle, action, children }) {
  return (
    <section className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5">
      {(title || subtitle || action) && (
        <header className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title && (
              <h4 className="text-sm font-bold text-gray-900">{title}</h4>
            )}
            {subtitle && (
              <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      {label && (
        <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
          {label}
        </span>
      )}
      <div className={label ? 'mt-1' : ''}>{children}</div>
      {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
    </label>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2">
      <div className="min-w-0">
        {label && (
          <p className="text-sm font-semibold text-gray-900">{label}</p>
        )}
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Segmented({ value, onChange, options, disabled }) {
  return (
    <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => !disabled && onChange(o.value)}
          disabled={disabled}
          className={cx(
            'px-3 py-1.5 text-xs font-semibold rounded-md transition',
            value === o.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700',
            disabled && 'opacity-60 cursor-not-allowed'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const MAX_EVENT_MEDIA = 10;
const MAX_SPACES_PER_EVENT = 100;
// EventCreation forces € too: the booking transaction is in EUR whatever the
// viewer's display-currency preference says.
const CURRENCY_SYMBOL = '€';
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Same guard as the creation form: number inputs accept "e"/"+"/"-" natively,
// which reads back as an empty value instead of the typo it is.
const blockNumberKeys = (e) => {
  if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

const makeId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// ---------- helpers ----------
// Category ids can arrive as strings (legacy EventCreation payloads) or
// numbers (CategorySelectorGrid). Coerce to int before deduping so a Set
// doesn't keep both "2" and 2.
const unique = (arr) => {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    if (x === undefined || x === null) continue;
    const n = typeof x === 'number' ? x : parseInt(x, 10);
    if (!Number.isFinite(n) || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
};

const getEventWindow = (dateObj) => {
  if (!dateObj || !dateObj.type) return { start: null, end: null };

  if (dateObj.type === 'fixed') {
    const start = moment(
      `${dateObj.startDate} ${dateObj.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${dateObj.endDate} ${dateObj.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  if (
    dateObj.type === 'segmented' &&
    Array.isArray(dateObj.days) &&
    dateObj.days.length
  ) {
    const days = [...dateObj.days].filter((d) => d?.date);
    if (!days.length) return { start: null, end: null };
    const sorted = days.sort((a, b) => moment(a.date).diff(moment(b.date)));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    const start = moment(
      `${first.date} ${first.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${last.date} ${last.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  return { start: null, end: null };
};

const normalizeCategories = (raw) => {
  if (!raw) return { macros: [], micros: [] };

  // { macros, micros }
  if (!Array.isArray(raw) && typeof raw === 'object') {
    const macros = Array.isArray(raw.macros) ? raw.macros : [];
    const micros = Array.isArray(raw.micros) ? raw.micros : [];
    return { macros: unique(macros), micros: unique(micros) };
  }

  // array forms
  if (Array.isArray(raw)) {
    const macros = [];
    const micros = [];
    for (const item of raw) {
      if (item && typeof item === 'object') {
        if ('macroCategoryId' in item) macros.push(item.macroCategoryId);
        if ('macroCategory' in item) macros.push(item.macroCategory);

        if ('microCategoryIds' in item) {
          const arr = Array.isArray(item.microCategoryIds)
            ? item.microCategoryIds
            : [item.microCategoryIds];
          micros.push(...arr);
        }
        if ('microCategory' in item) {
          const arr = Array.isArray(item.microCategory)
            ? item.microCategory
            : [item.microCategory];
          micros.push(...arr.filter((v) => v !== undefined && v !== null));
        }
      } else {
        macros.push(item);
      }
    }
    return { macros: unique(macros), micros: unique(micros) };
  }

  return { macros: [], micros: [] };
};

const toFixedDateState = (dateObj) => {
  if (!dateObj || dateObj.type !== 'fixed') {
    return { startDate: '', endDate: '', openHours: '', closeHours: '' };
  }
  return {
    startDate: dateObj.startDate || '',
    endDate: dateObj.endDate || '',
    openHours: dateObj.openHours || '',
    closeHours: dateObj.closeHours || '',
  };
};

const toSegmentedDateState = (dateObj) => {
  if (!dateObj || dateObj.type !== 'segmented' || !Array.isArray(dateObj.days))
    return [{ date: '', openHours: '', closeHours: '' }];

  return dateObj.days.map((d) => ({
    date: d.date || '',
    openHours: d.openHours || '',
    closeHours: d.closeHours || '',
  }));
};

const UploadButton = ({
  id,
  label,
  Icon,
  accept,
  multiple = false,
  disabled = false,
  onChange,
  hint,
  rightSlot,
}) => (
  // `relative` anchors the sr-only (absolutely positioned) input to this row.
  // Without it the input resolves against the modal panel, and the focus the
  // browser gives it on label click scrolls the panel away from the form.
  <div className="relative flex items-center gap-3">
    <input
      id={id}
      type="file"
      accept={accept}
      multiple={multiple}
      className="sr-only"
      disabled={disabled}
      onChange={onChange}
    />

    <label
      htmlFor={id}
      className={`inline-flex items-center justify-center px-4 py-2 bg-yellow-500 text-white rounded-lg select-none
      ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-yellow-600'}`}
    >
      {Icon && <Icon className="w-5 h-5 mr-2" />}
      {label}
      {hint ? <span className="ml-2 text-xs opacity-80">{hint}</span> : null}
    </label>

    {rightSlot}
  </div>
);

const getComp = (components, type) =>
  components?.find((c) => c.types?.includes(type));

const getAnyComp = (components, types) =>
  components?.find((c) => types.some((t) => c.types?.includes(t)));

const extractEventLocation = (components = []) => {
  const streetNumber = getComp(components, 'street_number')?.long_name || '';
  const route = getComp(components, 'route')?.long_name || '';

  const addressLine = [streetNumber, route].filter(Boolean).join(' ').trim();

  const neighborhood =
    getAnyComp(components, [
      'neighborhood',
      'sublocality',
      'sublocality_level_1',
      'sublocality_level_2',
    ])?.long_name || '';

  const city =
    getAnyComp(components, [
      'locality',
      'postal_town',
      'administrative_area_level_3',
      'administrative_area_level_2',
    ])?.long_name || '';

  const region =
    getComp(components, 'administrative_area_level_1')?.long_name || '';

  const postalCode = getComp(components, 'postal_code')?.long_name || '';

  const countryComp = getComp(components, 'country');
  const country = countryComp?.long_name || '';
  const countryCode = countryComp?.short_name || '';

  return {
    address: addressLine,
    neighborhood,
    city,
    region,
    postalCode,
    country,
    countryCode,
  };
};

// ---------- component ----------
const EventEditForm = ({ event, onCancelled, onDeleted }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert, confirm } = useAlert();

  const { bookings = [], loading: bookingsLoading } = useSelector(
    (s) => s.bookings || {}
  );

  // ---- Cropper (avatar) ----
  const {
    imagePreview,
    croppedImage,
    croppedImagePreview,
    isCropping,
    handleImageChange: handleAvatarPickForCrop,
    handleCropSave,
    resetImage: resetAvatarCrop,
    setIsCropping,
  } = useImageCropper();

  const [avatarFileName, setAvatarFileName] = useState('');

  // ---- Existing additional media normalization ----
  const originalMedia = useMemo(() => {
    const raw =
      event?.media ||
      event?.images ||
      event?.additionalImages ||
      event?.eventMedia ||
      [];

    if (!Array.isArray(raw)) return [];

    return raw
      .map((x) => (typeof x === 'string' ? x : x?.url || x?.image || x?.src))
      .filter(Boolean);
  }, [event]);

  const [existingMedia, setExistingMedia] = useState(originalMedia);
  // new media items: { id, file, preview }
  const [newMedia, setNewMedia] = useState([]);
  const previewUrlsRef = useRef(new Set());

  // Re-sync media when event changes
  useEffect(() => {
    setExistingMedia(originalMedia);

    // clear newMedia previews
    setNewMedia((prev) => {
      prev.forEach((x) => {
        if (x?.preview) {
          try {
            URL.revokeObjectURL(x.preview);
          } catch {}
          previewUrlsRef.current.delete(x.preview);
        }
      });
      return [];
    });

    // reset avatar crop state when switching events
    resetAvatarCrop();
    setAvatarFileName('');
    setRemoveExistingAvatar(false);
    setRemoveExistingCover(false);

    // location fields
    setLocation(event?.location || '');
    const label = event?.formattedAddress || event?.location || '';
    const pid = event?.locationPlaceId || '';

    setLocationOption(
      label ? { label, value: { place_id: pid, description: label } } : null
    );

    setLocationPlaceId(pid);
    setFormattedAddress(event?.formattedAddress || '');
    setAddress(event?.address || '');
    setNeighborhood(event?.neighborhood || '');
    setCity(event?.city || '');
    setRegion(event?.region || '');
    setPostalCode(event?.postalCode || '');
    setCountry(event?.country || '');
    setCountryCode(event?.countryCode || '');
    setLat(event?.lat != null ? Number(event.lat) : null);
    setLng(event?.lng != null ? Number(event.lng) : null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, originalMedia]);

  // cleanup previews on unmount
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      previewUrlsRef.current.clear();
    };
  }, []);

  // Load bookings to decide locking rules
  useEffect(() => {
    if (event?.id) dispatch(getEventBookings(event.id));
  }, [dispatch, event?.id]);

  const hasLockedBookings = useMemo(
    () =>
      bookings?.some(
        (b) =>
          b.status === 'Approved' ||
          b.status === 'Paid' ||
          (b.paymentStatus || '').toLowerCase() === 'paid'
      ),
    [bookings]
  );

  const [, setMapsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const setReady = () => !cancelled && setMapsReady(true);

    if (window.google?.maps?.places) {
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!API_KEY) {
      console.warn('Missing Google Maps API key — skipping Google loader.');
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!window.__gmapsPromise) {
      const loader = new Loader({
        apiKey: API_KEY,
        libraries: ['places'],
        id: 'gmaps-places',
        language: 'en', // or (i18n.language||'en').split('-')[0] if you want
      });
      window.__gmapsPromise = loader.load();
    }

    window.__gmapsPromise.then(setReady).catch((err) => {
      console.error('Google Maps JS failed to load', err);
      setReady();
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const placesAvailable = !!window.google?.maps?.places;

  const onLocationChange = async (place) => {
    // cleared
    if (!place) {
      setLocationOption(null);
      setLocation('');
      setLocationPlaceId('');
      setFormattedAddress('');
      setAddress('');
      setNeighborhood('');
      setCity('');
      setRegion('');
      setPostalCode('');
      setCountry('');
      setCountryCode('');
      setLat(null);
      setLng(null);
      return;
    }

    setLocationOption(place);

    const placeId = place?.value?.place_id || '';
    setLocationPlaceId(placeId);

    // good default even before geocode resolves
    const fallbackLabel = place?.label || '';
    setLocation(fallbackLabel);

    if (!placeId || !API_KEY) return;

    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${API_KEY}`
      );
      const data = await resp.json();
      const result = data?.results?.[0];

      const components = result?.address_components || [];
      const parsed = extractEventLocation(components);

      const fa = result?.formatted_address || fallbackLabel;

      setFormattedAddress(fa);
      setLocation(fa); // keep your required `location` string aligned with the formatted address

      setAddress(parsed.address);
      setNeighborhood(parsed.neighborhood);
      setCity(parsed.city);
      setRegion(parsed.region);
      setPostalCode(parsed.postalCode);
      setCountry(parsed.country);
      setCountryCode((parsed.countryCode || '').toUpperCase());

      const coords = result?.geometry?.location;
      setLat(typeof coords?.lat === 'number' ? coords.lat : null);
      setLng(typeof coords?.lng === 'number' ? coords.lng : null);

      // keep label in UI nice
      setLocationOption({ ...place, label: fa });
    } catch (e) {
      console.error('Event location geocode error:', e);
      // keep fallbackLabel already applied
    }
  };

  // Time / status locks
  const normalizedDate = useMemo(
    () => normalizeEventDate(event?.date),
    [event?.date]
  );
  const { start, end } = useMemo(
    () => getEventWindow(normalizedDate),
    [normalizedDate]
  );
  const now = moment();
  const isActiveNow =
    start && end && now.isSameOrAfter(start) && now.isBefore(end);
  const isPastNow = end && now.isSameOrAfter(end);
  const isTimeLocked = !!(isActiveNow || isPastNow);

  const statusLocked = ['Cancelled', 'Completed'].includes(event?.status);
  const fullLock = isTimeLocked || statusLocked;
  const coreLocked = fullLock || hasLockedBookings;

  const canEditAll = !fullLock;
  const canEditCore = !coreLocked;
  // The regulation document stays editable while the event is live or ended —
  // the API only rejects updates for Cancelled/Completed events.
  const canEditDocs = !statusLocked;

  // ----- cancellation (T2B-402) -----
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bookings that still hold a live claim on THIS event. The bookings slice is
  // shared, so it can still carry rows from the last event the promoter
  // opened — hence the eventId filter. Effective status, not the stored one:
  // a Pending row past its payment window is already dead, and the API's
  // guard reads it the same way.
  const liveBookingCount = useMemo(
    () =>
      (bookings || []).filter(
        (b) =>
          Number(b?.eventId) === Number(event?.id) &&
          ['pending', 'approved', 'paid'].includes(
            getEffectiveBookingStatus(b) || ''
          )
      ).length,
    [bookings, event?.id]
  );

  // Offered only for an event that hasn't run yet and nobody has booked. The
  // API enforces the same rule — this just keeps the promoter from asking.
  const isDraft = String(event?.status || '').toLowerCase() === 'draft';
  const canCancelEvent =
    !isDraft &&
    !statusLocked &&
    !isPastNow &&
    !bookingsLoading &&
    liveBookingCount === 0;

  // ----- basic fields -----
  const [name, setName] = useState(event?.name || '');
  const [location, setLocation] = useState(event?.location || '');
  const [locationOption, setLocationOption] = useState(null);

  const [locationPlaceId, setLocationPlaceId] = useState(
    event?.locationPlaceId || ''
  );
  const [formattedAddress, setFormattedAddress] = useState(
    event?.formattedAddress || ''
  );
  const [address, setAddress] = useState(event?.address || '');
  const [neighborhood, setNeighborhood] = useState(event?.neighborhood || '');
  const [city, setCity] = useState(event?.city || '');
  const [region, setRegion] = useState(event?.region || '');
  const [postalCode, setPostalCode] = useState(event?.postalCode || '');
  const [country, setCountry] = useState(event?.country || '');
  const [countryCode, setCountryCode] = useState(event?.countryCode || '');
  const [lat, setLat] = useState(
    typeof event?.lat === 'number'
      ? event.lat
      : event?.lat
        ? Number(event.lat)
        : null
  );
  const [lng, setLng] = useState(
    typeof event?.lng === 'number'
      ? event.lng
      : event?.lng
        ? Number(event.lng)
        : null
  );

  // ----- date -----
  const initialType =
    normalizedDate?.type === 'segmented' ? 'segmented' : 'fixed';
  const [dateType, setDateType] = useState(initialType);
  const [fixed, setFixed] = useState(toFixedDateState(normalizedDate));
  const [segmented, setSegmented] = useState(
    toSegmentedDateState(normalizedDate)
  );

  // ----- descriptions -----
  const [shortDescription, setShortDescription] = useState(
    event?.shortDescription || ''
  );
  const [longDescription, setLongDescription] = useState(
    event?.longDescription || ''
  );

  // ----- uploads -----
  const [coverFile, setCoverFile] = useState(null);
  const [regDocFile, setRegDocFile] = useState(null);

  // cover preview URL (avoid leaking object urls)
  const [coverPreview, setCoverPreview] = useState(null);
  const [removeExistingAvatar, setRemoveExistingAvatar] = useState(false);
  const [removeExistingCover, setRemoveExistingCover] = useState(false);

  useEffect(() => {
    if (!coverFile) {
      setCoverPreview(null);
      return;
    }
    const url = URL.createObjectURL(coverFile);
    setCoverPreview(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };
  }, [coverFile]);

  // ----- spaces (add-only) -----
  // Space.quantity is the REMAINING availability, so extra capacity is sent as
  // a delta the API applies atomically — an absolute value would hand back a
  // unit sold while this form was open. Existing names/prices/sizes stay put:
  // brands are looking at them right now, and placements link by name.
  // Keyed on the spaces array itself, not the whole event: a parent re-render
  // that hands back the same data must not wipe the drafts below.
  const eventSpaces = useMemo(() => {
    const list = Array.isArray(event?.spaces) ? event.spaces : event?.Spaces;
    return (Array.isArray(list) ? list : []).filter(Boolean);
  }, [event?.spaces, event?.Spaces]);

  // Server state for the spaces, refreshed in place after a save — the modal
  // holds on to the `event` prop it opened with, so the list would otherwise
  // keep showing pre-save quantities until the dashboard is reopened.
  const [spaceRows, setSpaceRows] = useState(eventSpaces);
  // spaceId → units to add, as typed
  const [spaceAdds, setSpaceAdds] = useState({});
  // drafts for brand-new space types: { key, name, size, quantity, price, description }
  const [newSpaces, setNewSpaces] = useState([]);

  useEffect(() => {
    setSpaceRows(eventSpaces);
    setSpaceAdds({});
    setNewSpaces([]);
  }, [eventSpaces]);

  const addedUnitsFor = (spaceId) => {
    const n = parseInt(spaceAdds[spaceId], 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const spacesDirty =
    newSpaces.length > 0 ||
    spaceRows.some((s) => addedUnitsFor(s?.id) > 0);

  const setSpaceAdd = (spaceId, value) =>
    setSpaceAdds((prev) => ({ ...prev, [spaceId]: value }));

  const addNewSpace = () =>
    setNewSpaces((prev) => [
      ...prev,
      {
        key: makeId(),
        name: '',
        size: '',
        quantity: '',
        price: '',
        description: '',
      },
    ]);

  const updateNewSpace = (key, field, value) =>
    setNewSpaces((prev) =>
      prev.map((s) => (s.key === key ? { ...s, [field]: value } : s))
    );

  const removeNewSpace = (key) =>
    setNewSpaces((prev) => prev.filter((s) => s.key !== key));

  // ----- floorplan & placements -----
  const initialPlacements = useMemo(
    () => (Array.isArray(event?.placements) ? event.placements : []),
    [event]
  );
  const [floorplanFile, setFloorplanFile] = useState(null);
  const [floorplanPreview, setFloorplanPreview] = useState(null);
  const [removeFloorplan, setRemoveFloorplan] = useState(false);
  // Live pin list from the editor. Same reference as initialPlacements until
  // the promoter actually changes something — that's the dirty check.
  const [placementsDraft, setPlacementsDraft] = useState(initialPlacements);

  useEffect(() => {
    if (!floorplanFile) {
      setFloorplanPreview(null);
      return;
    }
    const url = URL.createObjectURL(floorplanFile);
    setFloorplanPreview(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };
  }, [floorplanFile]);

  // Pins held by an actively blocking booking (Approved not yet expired, or
  // Paid) mirror the API's isBookingActivelyBlocking rule — they can't be
  // moved or removed, and the floorplan image can't be swapped from under
  // them.
  const lockedPlacementIds = useMemo(() => {
    const ids = [];
    for (const b of bookings || []) {
      if (!b?.placementId) continue;
      const paid =
        b.status === 'Paid' ||
        String(b.paymentStatus || '').toLowerCase() === 'paid';
      const approved = b.status === 'Approved';
      if (!paid && !approved) continue;
      if (!paid && b.expireAt && new Date(b.expireAt) <= new Date()) continue;
      ids.push(b.placementId);
    }
    return ids;
  }, [bookings]);
  const hasLockedPins = lockedPlacementIds.length > 0;

  // The editor derives per-space availability as quantity − pins, but
  // Space.quantity is the REMAINING inventory once bookings consume it.
  // Add the locked pins back so the pin budget approximates total capacity.
  const editorSpaces = useMemo(() => {
    const spaces = spaceRows;
    const lockedSet = new Set(lockedPlacementIds);
    const lockedCountBySpace = new Map();
    for (const p of initialPlacements) {
      if (!lockedSet.has(p?.id)) continue;
      const sid = Number(p?.spaceId);
      if (!Number.isFinite(sid)) continue;
      lockedCountBySpace.set(sid, (lockedCountBySpace.get(sid) || 0) + 1);
    }
    return spaces.map((s) => ({
      name: s.name,
      spaceId: s.id,
      quantity:
        (Number(s.quantity) || 0) + (lockedCountBySpace.get(Number(s.id)) || 0),
    }));
  }, [spaceRows, lockedPlacementIds, initialPlacements]);

  const placementsChanged = placementsDraft !== initialPlacements;
  const floorplanDirty =
    !!floorplanFile || removeFloorplan || placementsChanged;

  const currentFloorplanUrl = removeFloorplan
    ? null
    : floorplanPreview || event?.floorplanUrl || null;

  // Floor planning is opt-in at creation time: an event that was created
  // without a plan can't gain one here, so the whole block stays hidden.
  const hasFloorplan = !!event?.floorplanUrl;

  // ----- socials -----
  // Canonical store is event.socials (JSONB); legacy flat fields kept as
  // fallback for anything cached before the column existed.
  const eventSocials = event?.socials || {};
  const [facebookUrl, setFacebookUrl] = useState(
    eventSocials.facebook || event?.facebookUrl || ''
  );
  const [instagramUrl, setInstagramUrl] = useState(
    eventSocials.instagram || event?.instagramUrl || ''
  );
  const [tiktokUrl, setTiktokUrl] = useState(
    eventSocials.tiktok || event?.tiktokUrl || ''
  );
  const [twitterUrl, setTwitterUrl] = useState(
    eventSocials.twitter || event?.twitterUrl || ''
  );

  const initialCustom = Array.isArray(eventSocials.others)
    ? eventSocials.others
    : Array.isArray(event?.customSocials)
      ? event.customSocials
      : [];
  const [customSocials, setCustomSocials] = useState(
    initialCustom.map((x) => ({ label: x.label || '', url: x.url || '' }))
  );

  const addCustomSocial = () =>
    setCustomSocials((prev) => [...prev, { label: '', url: '' }]);
  const removeCustomSocial = (i) =>
    setCustomSocials((prev) => prev.filter((_, idx) => idx !== i));
  const updateCustomSocial = (i, key, val) =>
    setCustomSocials((prev) =>
      prev.map((row, idx) => (idx === i ? { ...row, [key]: val } : row))
    );

  // ----- categories (append-only) -----
  const baseCats = useMemo(
    () => normalizeCategories(event?.categoriesAllowed),
    [event?.categoriesAllowed]
  );
  const baseMacroSet = useMemo(() => new Set(baseCats.macros), [baseCats]);
  const baseMicroSet = useMemo(() => new Set(baseCats.micros), [baseCats]);

  const [selMacros, _setSelMacros] = useState(baseCats.macros);
  const [selMicros, _setSelMicros] = useState(baseCats.micros);

  const setSelMacros = (updater) => {
    _setSelMacros((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return unique([...(next || []), ...baseMacroSet]);
    });
  };
  const setSelMicros = (updater) => {
    _setSelMicros((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return unique([...(next || []), ...baseMicroSet]);
    });
  };

  // ----- utilities (append-only) -----
  const AMENITY_OPTIONS = useMemo(
    () =>
      UTILITIES.map((u) => ({
        label: getUtilityLabel(u.id, i18n.language),
        value: u.id,
      })),
    [i18n.language]
  );

  const existingUtilities = useMemo(
    () => (Array.isArray(event?.utilities) ? event.utilities : []),
    [event?.utilities]
  );
  const baseUtilSet = useMemo(
    () => new Set(existingUtilities),
    [existingUtilities]
  );
  const [addedUtils, setAddedUtils] = useState(new Set());

  const toggleAmenity = (slug) => {
    if (baseUtilSet.has(slug)) return; // cannot remove existing
    setAddedUtils((prev) => {
      const n = new Set(prev);
      if (n.has(slug)) n.delete(slug);
      else n.add(slug);
      return n;
    });
  };

  // ----- additional media handlers -----
  const remainingSlots = Math.max(
    0,
    MAX_EVENT_MEDIA - (existingMedia.length + newMedia.length)
  );

  const showUploadErrors = (uploadErrors) => {
    showAlert({
      type: 'warning',
      title: t('upload.error_title', { defaultValue: 'Upload issue' }),
      message: uploadErrors.join('\n'),
      primaryAction: { label: t('common.ok') },
    });
  };

  // Files currently attached to the form (existing remote URLs excluded —
  // they are not re-uploaded), for the combined-size budget.
  const attachedUploadFiles = () => [
    croppedImage,
    coverFile,
    regDocFile,
    floorplanFile,
    ...newMedia.map((item) => item?.file),
  ];

  const attachedUploadBytes = () =>
    attachedUploadFiles()
      .filter(Boolean)
      .reduce((sum, f) => sum + (f.size || 0), 0);

  // Validates a single freshly picked file; returns it when valid, else
  // shows the errors, clears the input, and returns null.
  const guardPickedFile = (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return null;
    const { accepted, errors: uploadErrors } = validateFiles([file], {
      kind,
      existingBytes: attachedUploadBytes(),
      t,
    });
    if (uploadErrors.length) {
      showUploadErrors(uploadErrors);
      e.target.value = '';
      return null;
    }
    return accepted[0] || null;
  };

  // ----- spaces validation & save -----
  // Same rules the API enforces on /spaces/bulk, surfaced before the request so
  // the promoter sees every problem at once instead of one 400 at a time.
  const collectSpaceErrors = () => {
    const errs = [];
    const taken = new Set(
      spaceRows
        .map((s) => String(s?.name || '').trim().toLowerCase())
        .filter(Boolean)
    );

    if (spaceRows.length + newSpaces.length > MAX_SPACES_PER_EVENT) {
      errs.push(
        t('dashboard.em.spaces_max_error', {
          defaultValue: 'An event can have at most {{max}} space types.',
          max: MAX_SPACES_PER_EVENT,
        })
      );
    }

    newSpaces.forEach((s, i) => {
      const name = (s.name || '').trim();
      const label =
        name ||
        t('dashboard.em.spaces_new_index', {
          defaultValue: 'New space {{n}}',
          n: i + 1,
        });
      const fail = (message) => errs.push(`${label}: ${message}`);

      if (!name) {
        fail(
          t('dashboard.em.spaces_error_name', {
            defaultValue: 'a name is required.',
          })
        );
      } else if (taken.has(name.toLowerCase())) {
        fail(
          t('dashboard.em.spaces_error_name_taken', {
            defaultValue: 'that name is already used by another space.',
          })
        );
      } else {
        taken.add(name.toLowerCase());
      }

      const size = Number(s.size);
      if (!Number.isFinite(size) || size <= 0) {
        fail(
          t('dashboard.em.spaces_error_size', {
            defaultValue: 'size must be greater than 0.',
          })
        );
      }

      const quantity = parseInt(s.quantity, 10);
      if (!Number.isInteger(quantity) || quantity <= 0) {
        fail(
          t('dashboard.em.spaces_error_quantity', {
            defaultValue: 'availability must be a whole number above 0.',
          })
        );
      }

      const price = Number(s.price);
      if (!Number.isFinite(price) || price <= 0) {
        fail(
          t('dashboard.em.spaces_error_price', {
            defaultValue: 'price must be greater than 0.',
          })
        );
      } else if (Math.abs(price * 100 - Math.round(price * 100)) > 1e-6) {
        fail(
          t('dashboard.em.spaces_error_price_decimals', {
            defaultValue: 'price can have at most 2 decimals.',
          })
        );
      }
    });

    return errs;
  };

  // Runs before the event PUT so the refreshed promoter-events list already
  // carries the new inventory. Applied changes are cleared as soon as the API
  // confirms them — a later failure on the event itself must not replay them.
  const saveSpaceChanges = async () => {
    const updated = [];
    for (const s of spaceRows) {
      const delta = addedUnitsFor(s?.id);
      if (!delta || !s?.id) continue;
      const row = await dispatch(updateSpace(s.id, { quantityDelta: delta }));
      if (row) updated.push(row);
    }

    let created = [];
    if (newSpaces.length) {
      const rows = newSpaces.map((s) => ({
        eventId: event.id,
        name: (s.name || '').trim(),
        description: (s.description || '').trim(),
        size: Number(s.size),
        quantity: parseInt(s.quantity, 10),
        price: Number(s.price),
      }));
      const res = await dispatch(createSpaces(rows));
      created = Array.isArray(res) ? res : [];
    }

    setSpaceRows((prev) => {
      const byId = new Map(updated.map((r) => [String(r.id), r]));
      return [...prev.map((s) => byId.get(String(s?.id)) || s), ...created];
    });
    setSpaceAdds({});
    setNewSpaces([]);
  };

  // ----- floorplan handlers -----
  const onPickFloorplan = (e) => {
    const f = guardPickedFile(e, 'image');
    if (!f) return;
    setFloorplanFile(f);
    setRemoveFloorplan(false);
    // A different image means the old pin coordinates are meaningless —
    // start from a clean plan (blocked entirely while pins are locked).
    setPlacementsDraft([]);
    e.target.value = '';
  };

  const onRemoveFloorplan = () => {
    setFloorplanFile(null);
    setRemoveFloorplan(true);
    setPlacementsDraft([]);
  };

  const onPickMedia = (e) => {
    const input = e.target;
    const picked = Array.from(input.files || []);
    if (!picked.length) return;

    const { accepted: files, errors: uploadErrors } = validateFiles(picked, {
      kind: 'image',
      existingBytes: attachedUploadBytes(),
      t,
    });
    if (uploadErrors.length) showUploadErrors(uploadErrors);
    if (!files.length) {
      input.value = '';
      return;
    }

    const allowed = files.slice(0, remainingSlots);
    if (!allowed.length) {
      input.value = '';
      return;
    }

    const items = allowed.map((file) => {
      const id = makeId();
      const preview = URL.createObjectURL(file);
      previewUrlsRef.current.add(preview);
      return { id, file, preview };
    });

    setNewMedia((prev) => [...prev, ...items]);
    input.value = '';
  };

  const removeExistingMedia = (url) => {
    setExistingMedia((prev) => prev.filter((u) => u !== url));
  };

  const removeNewMedia = (id) => {
    setNewMedia((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.preview) {
        try {
          URL.revokeObjectURL(item.preview);
        } catch {}
        previewUrlsRef.current.delete(item.preview);
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  const handleCancelEvent = useCallback(async () => {
    const ok = await confirm({
      title: t('event_edit.cancel_confirm_title', 'Cancel this event?'),
      message: t(
        'event_edit.cancel_confirm_message',
        'The event will be taken off There2be and can no longer be booked. This cannot be undone.'
      ),
      confirmLabel: t('event_edit.cancel_confirm_label', 'Cancel event'),
      confirmIntent: 'danger',
      cancelLabel: t('event_edit.cancel_keep_label', 'Keep event'),
    });
    if (!ok) return;

    setCancelling(true);
    try {
      await dispatch(cancelEvent(event.id, { lang: i18n.language }));
      showAlert({
        type: 'success',
        title: t('event_edit.cancel_success_title', 'Event cancelled'),
        message: t(
          'event_edit.cancel_success_message',
          'Your event has been cancelled and is no longer listed.'
        ),
        primaryAction: { label: t('common.ok') },
      });
      // The modal would otherwise sit on a now-stale copy of the event.
      onCancelled?.();
    } catch (err) {
      showAlert({
        type: 'error',
        title: t('event_edit.cancel_error_title', 'Could not cancel event'),
        // The API is the authority here: it may refuse because a booking
        // landed between the page load and the click.
        message:
          err?.response?.data?.message ||
          t(
            'event_edit.cancel_error_message',
            'Something went wrong while cancelling the event. Please try again.'
          ),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setCancelling(false);
    }
  }, [confirm, dispatch, event?.id, i18n.language, onCancelled, showAlert, t]);

  // A draft is deleted outright — nobody else has seen it, so there is no one
  // to tell and nothing to keep. Published events are cancelled instead (above).
  const handleDeleteDraft = useCallback(async () => {
    const ok = await confirm({
      title: t('event_edit.delete_draft_confirm_title'),
      message: t('event_edit.delete_draft_confirm_message'),
      confirmLabel: t('event_edit.delete_draft_action'),
      confirmIntent: 'danger',
      cancelLabel: t('event_edit.delete_draft_keep'),
    });
    if (!ok) return;

    setDeleting(true);
    try {
      await dispatch(deleteEvent(event.id));
      showAlert({
        type: 'success',
        title: t('event_edit.delete_draft_success_title'),
        message: t('event_edit.delete_draft_success_message'),
        primaryAction: { label: t('common.ok') },
      });
      // The event no longer exists; close the modal that was showing it.
      onDeleted?.();
    } catch (err) {
      showAlert({
        type: 'error',
        title: t('event_edit.delete_draft_error_title'),
        message: t('event_edit.delete_draft_error_message'),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setDeleting(false);
    }
  }, [confirm, dispatch, event?.id, onDeleted, showAlert, t]);

  // ----- save -----
  const [saving, setSaving] = useState(false);

  const handleDiscard = useCallback(async () => {
    const ok = await confirm({
      type: 'confirm',
      title: t('alert_dialog.edit.discard_title', 'Discard changes?'),
      message: t(
        'alert_dialog.edit.discard_confirm',
        'Your unsaved changes will be lost.'
      ),
      confirmLabel: t('alert_dialog.edit.discard_confirm_label', 'Discard'),
      confirmIntent: 'danger',
      cancelLabel: t('common.keep_editing', 'Keep editing'),
    });
    if (!ok) return;
    setName(event?.name || '');
    setLocation(event?.location || '');
    const fa = event?.formattedAddress || event?.location || '';
    const pid = event?.locationPlaceId || '';
    setLocationOption(
      fa ? { label: fa, value: { place_id: pid, description: fa } } : null
    );
    setLocationPlaceId(pid);
    setFormattedAddress(event?.formattedAddress || '');
    setAddress(event?.address || '');
    setNeighborhood(event?.neighborhood || '');
    setCity(event?.city || '');
    setRegion(event?.region || '');
    setPostalCode(event?.postalCode || '');
    setCountry(event?.country || '');
    setCountryCode(event?.countryCode || '');
    setLat(event?.lat != null ? Number(event.lat) : null);
    setLng(event?.lng != null ? Number(event.lng) : null);

    setDateType(normalizedDate?.type === 'segmented' ? 'segmented' : 'fixed');
    setFixed(toFixedDateState(normalizedDate));
    setSegmented(toSegmentedDateState(normalizedDate));

    setShortDescription(event?.shortDescription || '');
    setLongDescription(event?.longDescription || '');

    setCoverFile(null);
    setRegDocFile(null);
    setRemoveExistingAvatar(false);
    setRemoveExistingCover(false);
    resetAvatarCrop();
    setAvatarFileName('');

    const s = event?.socials || {};
    setFacebookUrl(s.facebook || event?.facebookUrl || '');
    setInstagramUrl(s.instagram || event?.instagramUrl || '');
    setTiktokUrl(s.tiktok || event?.tiktokUrl || '');
    setTwitterUrl(s.twitter || event?.twitterUrl || '');
    const others = Array.isArray(s.others)
      ? s.others
      : Array.isArray(event?.customSocials)
        ? event.customSocials
        : [];
    setCustomSocials(
      others.map((x) => ({ label: x.label || '', url: x.url || '' }))
    );

    _setSelMacros(baseCats.macros);
    _setSelMicros(baseCats.micros);
    setAddedUtils(new Set());

    setExistingMedia(originalMedia);
    setNewMedia((prev) => {
      prev.forEach((x) => {
        if (x?.preview) {
          try {
            URL.revokeObjectURL(x.preview);
          } catch {}
          previewUrlsRef.current.delete(x.preview);
        }
      });
      return [];
    });

    // floorplan editing state — same reference as the memo so the
    // placementsChanged identity check starts out false
    setFloorplanFile(null);
    setRemoveFloorplan(false);
    setPlacementsDraft(initialPlacements);

    // spaces: only the unsaved additions are dropped — spaceRows already holds
    // what the server confirmed
    setSpaceAdds({});
    setNewSpaces([]);
  }, [
    event,
    normalizedDate,
    baseCats,
    originalMedia,
    resetAvatarCrop,
    initialPlacements,
    t,
    confirm,
  ]);

  // Canonical socials payload (same shape as Brand.socials). The form has no
  // inputs for website/linkedin, so whatever the event already has is kept.
  const buildSocialsPayload = () => ({
    website: eventSocials.website || null,
    linkedin: eventSocials.linkedin || null,
    facebook: (facebookUrl || '').trim() || null,
    instagram: (instagramUrl || '').trim() || null,
    tiktok: (tiktokUrl || '').trim() || null,
    twitter: (twitterUrl || '').trim() || null,
    others: customSocials
      .map(({ label, url }) => ({
        label: (label || '').trim(),
        url: (url || '').trim(),
      }))
      .filter((l) => l.label && l.url),
  });

  const buildDatePayload = () => {
    if (dateType === 'fixed') {
      return {
        type: 'fixed',
        startDate: fixed.startDate || '',
        endDate: fixed.endDate || '',
        openHours: fixed.openHours || '',
        closeHours: fixed.closeHours || '',
      };
    }
    return {
      type: 'segmented',
      days: segmented.map((d) => ({
        date: d.date || '',
        openHours: d.openHours || '',
        closeHours: d.closeHours || '',
      })),
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!event?.id) return;
    if (!canEditAll && !(canEditDocs && regDocFile)) return;

    const combinedError = combinedUploadError(attachedUploadFiles(), t);
    if (combinedError) {
      showUploadErrors([combinedError]);
      return;
    }

    const spaceErrors = canEditAll && spacesDirty ? collectSpaceErrors() : [];
    if (spaceErrors.length) {
      showAlert({
        type: 'warning',
        title: t('dashboard.em.spaces_invalid_title', {
          defaultValue: 'Check the spaces',
        }),
        message: spaceErrors.join('\n'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }

    setSaving(true);
    try {
      if (canEditAll && spacesDirty) await saveSpaceChanges();

      const removedMedia = originalMedia.filter(
        (u) => !existingMedia.includes(u)
      );
      const mediaChanged = removedMedia.length > 0 || newMedia.length > 0;

      const hasFiles =
        !!croppedImage ||
        !!coverFile ||
        !!regDocFile ||
        !!floorplanFile ||
        mediaChanged;

      // Pins whose space disappeared are dropped, mirroring the creation
      // flow — they could never be booked.
      const editorSpaceNames = new Set(editorSpaces.map((s) => s.name));
      const placementsToSend = removeFloorplan
        ? []
        : (placementsDraft || []).filter((p) =>
            editorSpaceNames.has(p?.spaceName)
          );

      // Array shape [{ macroCategoryId, microCategoryIds }] — same as event
      // creation, so the DB keeps a single canonical format.
      const categoriesAllowed = (selMacros || []).map((macroId) => {
        const macroDef = allCategories.find((c) => c.id === macroId);
        const ownMicroIds = new Set((macroDef?.micro || []).map((m) => m.id));
        return {
          macroCategoryId: macroId,
          microCategoryIds: (selMicros || []).filter((id) =>
            ownMicroIds.has(id)
          ),
        };
      });

      const utilities = Array.from(
        new Set([...existingUtilities, ...Array.from(addedUtils)])
      );

      if (!canEditAll) {
        // Time-locked event: only the regulation document may change, so send
        // nothing else — every other field must keep its stored value.
        const fd = new FormData();
        fd.append('regulatoryDocument', regDocFile);
        await dispatch(updateEvent(event.id, fd, { lang: i18n.language }));
      } else if (hasFiles) {
        const fd = new FormData();

        // core
        fd.append('name', name);

        // location
        fd.append('location', location);
        fd.append('locationPlaceId', locationPlaceId || '');
        fd.append('formattedAddress', formattedAddress || '');
        fd.append('address', address || '');
        fd.append('neighborhood', neighborhood || '');
        fd.append('city', city || '');
        fd.append('region', region || '');
        fd.append('postalCode', postalCode || '');
        fd.append('country', country || '');
        fd.append('countryCode', countryCode || '');

        if (typeof lat === 'number') fd.append('lat', String(lat));
        if (typeof lng === 'number') fd.append('lng', String(lng));

        fd.append('date', JSON.stringify(buildDatePayload()));

        // text
        fd.append('shortDescription', shortDescription);
        fd.append('longDescription', longDescription);

        // categories/utilities
        fd.append('categoriesAllowed', JSON.stringify(categoriesAllowed));
        fd.append('utilities', JSON.stringify(utilities));

        // socials
        fd.append('socials', JSON.stringify(buildSocialsPayload()));

        // floorplan (only when actually touched — sending placements
        // unconditionally would rewrite the stored dots on every save)
        if (floorplanDirty) {
          fd.append('placements', JSON.stringify(placementsToSend));
          if (floorplanFile) fd.append('floorplanUrl', floorplanFile);
          else if (removeFloorplan) fd.append('floorplanUrl', '');
        }

        // avatar (cropped)
        if (croppedImage) {
          const fname = avatarFileName || `event-avatar-${event.id}.jpg`;
          const file =
            croppedImage instanceof File
              ? croppedImage
              : new File([croppedImage], fname, {
                  type: croppedImage.type || 'image/jpeg',
                });
          fd.append('avatar', file);
        }

        // cover/doc
        if (coverFile) fd.append('landscapeImage', coverFile);
        if (regDocFile) fd.append('regulatoryDocument', regDocFile);

        // additional images (brand-like semantics)
        fd.append('keepMedia', JSON.stringify(existingMedia));
        fd.append('removedMedia', JSON.stringify(removedMedia));
        newMedia.forEach(({ file }) => fd.append('media', file));

        // if core is locked, overwrite with original
        if (!canEditCore) {
          fd.set('name', event?.name || '');
          fd.set('location', event?.location || '');
          fd.set('date', JSON.stringify(event?.date || {}));

          fd.set('locationPlaceId', event?.locationPlaceId || '');
          fd.set('formattedAddress', event?.formattedAddress || '');
          fd.set('address', event?.address || '');
          fd.set('neighborhood', event?.neighborhood || '');
          fd.set('city', event?.city || '');
          fd.set('region', event?.region || '');
          fd.set('postalCode', event?.postalCode || '');
          fd.set('country', event?.country || '');
          fd.set('countryCode', event?.countryCode || '');
          if (event?.lat != null) fd.set('lat', String(event.lat));
          if (event?.lng != null) fd.set('lng', String(event.lng));
        }

        await dispatch(updateEvent(event.id, fd, { lang: i18n.language }));
      } else {
        const payload = {
          shortDescription,
          longDescription,
          categoriesAllowed,
          utilities,
          socials: buildSocialsPayload(),
        };

        if (floorplanDirty) {
          payload.placements = placementsToSend;
          // '' → null through the API's string normalization
          if (removeFloorplan) payload.floorplanUrl = '';
        }

        if (canEditCore) {
          payload.name = name;
          payload.location = location;
          payload.date = buildDatePayload();

          payload.locationPlaceId = locationPlaceId || null;
          payload.formattedAddress = formattedAddress || null;
          payload.address = address || null;
          payload.neighborhood = neighborhood || null;
          payload.city = city || null;
          payload.region = region || null;
          payload.postalCode = postalCode || null;
          payload.country = country || null;
          payload.countryCode = countryCode || null;
          payload.lat = typeof lat === 'number' ? lat : null;
          payload.lng = typeof lng === 'number' ? lng : null;
        }

        await dispatch(updateEvent(event.id, payload, { lang: i18n.language }));
      }

      showAlert({
        title: t('alert_dialog.edit.update_success_title', {
          defaultValue: 'Update successful',
        }),
        message: t('alert_dialog.edit.update_success_message', {
          defaultValue: 'Your changes were saved.',
        }),
        primaryAction: {
          label: t('alert_dialog.edit.update_success_primary_action', {
            defaultValue: 'OK',
          }),
        },
      });
    } catch (err) {
      console.error(err);
      const uploadMessage = uploadErrorMessage(err, t);
      showAlert({
        title: t('alert_dialog.edit.update_error_title', {
          defaultValue: 'Update failed',
        }),
        message:
          uploadMessage ||
          t('alert_dialog.edit.update_error_message', {
            defaultValue:
              'Something went wrong while saving. Please try again.',
          }),
        primaryAction: {
          label: t('alert_dialog.edit.update_error_primary_action', {
            defaultValue: 'OK',
          }),
        },
      });
    } finally {
      setSaving(false);
    }
  };

  // ----- lock banner copy -----
  const lockMessages = [];
  if (isActiveNow) {
    lockMessages.push(t('Event is active — editing disabled'));
  }
  if (isPastNow) {
    lockMessages.push(t('Event has ended — editing disabled'));
  }
  if (statusLocked && !isTimeLocked) {
    lockMessages.push(t('Editing disabled for this event status'));
  }
  if (!canEditAll && canEditDocs) {
    lockMessages.push(t('You can still update the regulation document'));
  }

  // While time-locked, saving is only possible once a new regulation
  // document has been picked.
  const canSubmit = canEditAll || (canEditDocs && !!regDocFile);

  const dateTypeOptions = [
    { value: 'fixed', label: t('event_create.schedule_fixed') },
    { value: 'segmented', label: t('event_create.schedule_segmented') },
  ];

  return (
    <>
      {/* Avatar Crop Modal */}
      {isCropping && imagePreview && (
        <ImageCropper
          imageSrc={imagePreview}
          aspect={1}
          shape="rect"
          onCropSave={(blob) => handleCropSave(blob)}
          onClose={() => {
            setIsCropping(false);
          }}
        />
      )}

      <form
        onSubmit={onSubmit}
        className="w-full max-w-4xl mx-auto pb-2"
      >
        {/* Status banner */}
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl bg-white ring-1 ring-black/5 shadow-sm px-4 py-3">
          <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
            {t('Status')}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-brand-soft text-amber-800 font-semibold">
            {event?.status}
          </span>
          {bookingsLoading && (
            <span className="text-xs text-gray-400">
              · {t('loading bookings…')}
            </span>
          )}
          {lockMessages.length > 0 && (
            <span className="text-xs text-amber-700">
              · {lockMessages.join(' · ')}
            </span>
          )}
        </div>

        <div className="space-y-5">
        <fieldset
          disabled={!canEditAll}
          className={cx(
            'space-y-5 min-w-0',
            !canEditAll && 'opacity-60 pointer-events-none'
          )}
        >
          {/* Section 1 — Cover & essentials */}
          <Section
            title={t('dashboard.em.section_essentials_title', {
              defaultValue: 'Cover & essentials',
            })}
            subtitle={t('dashboard.em.section_essentials_sub', {
              defaultValue: 'What brands see first in the feed.',
            })}
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Cover (landscape) thumbnail */}
              <div className="w-full sm:w-40 h-28 rounded-xl overflow-hidden ring-1 ring-black/5 relative group shrink-0 bg-gray-100">
                <input
                  key={
                    !removeExistingCover &&
                    (coverPreview || event?.landscapeImage)
                      ? 'has-main'
                      : 'no-main'
                  }
                  type="file"
                  accept={IMAGE_ACCEPT}
                  id="main-image-upload"
                  className="sr-only"
                  disabled={!canEditAll}
                  onChange={(e) => {
                    const f = guardPickedFile(e, 'image');
                    if (!f) return;
                    setRemoveExistingCover(false);
                    setCoverFile(f);
                    e.target.value = '';
                  }}
                />
                {!removeExistingCover &&
                (coverPreview || event?.landscapeImage) ? (
                  <>
                    <img
                      src={coverPreview || event?.landscapeImage}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2">
                      <label
                        htmlFor="main-image-upload"
                        className="opacity-0 group-hover:opacity-100 px-3 py-1.5 rounded-lg bg-white text-xs font-semibold text-gray-900 cursor-pointer transition"
                      >
                        {t('common.change', 'Change')}
                      </label>
                      {canEditAll && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (coverFile) setCoverFile(null);
                            else setRemoveExistingCover(true);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg bg-white/90 hover:bg-white transition"
                          aria-label={t('common.remove')}
                        >
                          <XCircleIcon className="w-4 h-4 text-rose-500" />
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <label
                    htmlFor="main-image-upload"
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  >
                    <CameraIcon className="w-8 h-8 text-gray-400" />
                  </label>
                )}
              </div>

              <div className="flex-1 min-w-0 space-y-3">
                <Field
                  label={t('event_create.event_name')}
                  hint={
                    !canEditCore && canEditAll
                      ? t('Locked due to confirmed bookings')
                      : undefined
                  }
                >
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!canEditCore}
                    className={INPUT_CLS}
                    placeholder={t('event_create.name_placeholder')}
                  />
                </Field>

                <Field label={t('event_create.location')}>
                  {placesAvailable ? (
                    <GooglePlacesAutocomplete
                      autocompletionRequest={{ types: ['geocode'] }}
                      selectProps={{
                        value: locationOption,
                        onChange: onLocationChange,
                        isDisabled: !canEditCore,
                        isClearable: true,
                        placeholder: t('event_create.location_placeholder'),
                        className: 'text-sm',
                        noOptionsMessage: () => t('homepage.no_options'),
                      }}
                    />
                  ) : (
                    <input
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      disabled={!canEditCore}
                      className={INPUT_CLS}
                      placeholder={t('event_create.location_placeholder')}
                    />
                  )}
                </Field>
              </div>
            </div>

            <Field label={t('event_create.short_description')}>
              <textarea
                value={shortDescription}
                onChange={(e) =>
                  setShortDescription(e.target.value.slice(0, 200))
                }
                rows={3}
                className={cx(INPUT_CLS, 'resize-none')}
                placeholder={t('event_create.short_description')}
              />
              <div className="text-[11px] text-gray-500 text-right mt-1">
                {shortDescription.length}/200
              </div>
            </Field>
          </Section>

          {/* Section 2 — Dates & hours */}
          <Section
            title={t('dashboard.em.section_dates_title', {
              defaultValue: 'Dates & hours',
            })}
          >
            <Row
              label={t('dashboard.em.schedule_type_label', {
                defaultValue: 'Schedule type',
              })}
              hint={t('dashboard.em.schedule_type_hint', {
                defaultValue: 'Fixed range or specific days.',
              })}
            >
              <Segmented
                value={dateType}
                onChange={(v) => canEditCore && setDateType(v)}
                options={dateTypeOptions}
                disabled={!canEditCore}
              />
            </Row>

            {dateType === 'fixed' ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: 'startDate', type: 'date' },
                  { key: 'endDate', type: 'date' },
                  { key: 'openHours', type: 'time' },
                  { key: 'closeHours', type: 'time' },
                ].map(({ key, type }) => (
                  <Field key={key} label={t(`event_create.${key}`)}>
                    <input
                      type={type}
                      value={fixed[key]}
                      onChange={(e) =>
                        setFixed((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      disabled={!canEditCore}
                      className={INPUT_CLS}
                    />
                  </Field>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {segmented.map((row, i) => (
                  <div
                    key={`seg-${i}`}
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[1fr_1fr_1fr_auto] gap-2"
                  >
                    <input
                      type="date"
                      value={row.date}
                      onChange={(e) =>
                        setSegmented((prev) =>
                          prev.map((d, idx) =>
                            idx === i ? { ...d, date: e.target.value } : d
                          )
                        )
                      }
                      disabled={!canEditCore}
                      className={INPUT_CLS}
                    />
                    <input
                      type="time"
                      value={row.openHours}
                      onChange={(e) =>
                        setSegmented((prev) =>
                          prev.map((d, idx) =>
                            idx === i
                              ? { ...d, openHours: e.target.value }
                              : d
                          )
                        )
                      }
                      disabled={!canEditCore}
                      className={INPUT_CLS}
                    />
                    <input
                      type="time"
                      value={row.closeHours}
                      onChange={(e) =>
                        setSegmented((prev) =>
                          prev.map((d, idx) =>
                            idx === i
                              ? { ...d, closeHours: e.target.value }
                              : d
                          )
                        )
                      }
                      disabled={!canEditCore}
                      className={INPUT_CLS}
                    />
                    {canEditCore ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSegmented((prev) =>
                            prev.filter((_, idx) => idx !== i)
                          )
                        }
                        className="px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 ring-1 ring-gray-200"
                      >
                        {t('common.remove')}
                      </button>
                    ) : (
                      <div />
                    )}
                  </div>
                ))}
                {canEditCore && (
                  <button
                    type="button"
                    onClick={() =>
                      setSegmented((prev) => [
                        ...prev,
                        { date: '', openHours: '', closeHours: '' },
                      ])
                    }
                    className="mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-800 bg-brand-soft hover:bg-brand-softer ring-1 ring-amber-200"
                  >
                    + {t('event_create.add_day')}
                  </button>
                )}
              </div>
            )}
          </Section>

          {/* Section 3 — Long description */}
          <Section
            title={t('dashboard.em.section_long_desc_title', {
              defaultValue: 'Long description',
            })}
            subtitle={t('dashboard.em.section_long_desc_sub', {
              defaultValue: 'The full pitch for your event page.',
            })}
          >
            <Field>
              <textarea
                value={longDescription}
                onChange={(e) =>
                  setLongDescription(e.target.value.slice(0, 10000))
                }
                rows={7}
                className={cx(INPUT_CLS, 'resize-y')}
                placeholder={t('event_create.long_description')}
              />
              <div className="text-[11px] text-gray-500 text-right mt-1">
                {longDescription.length}/10000
              </div>
            </Field>
          </Section>

          {/* Section 3b — Spaces. Add-only: capacity can grow and new space
              types can appear, but names, sizes and prices of live spaces stay
              as brands saw them when they booked. */}
          <Section
            title={t('dashboard.em.section_spaces_title', {
              defaultValue: 'Spaces',
            })}
            subtitle={t('dashboard.em.section_spaces_sub', {
              defaultValue:
                'Top up the availability of a space type, or add a new one. Existing names, sizes and prices stay as booked.',
            })}
            action={
              <button
                type="button"
                onClick={addNewSpace}
                disabled={
                  !canEditAll ||
                  spaceRows.length + newSpaces.length >= MAX_SPACES_PER_EVENT
                }
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-800 bg-brand-soft hover:bg-brand-softer ring-1 ring-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + {t('event_create.add_space_type', 'Add another space type')}
              </button>
            }
          >
            {spaceRows.length === 0 ? (
              <p className="text-xs text-gray-500">
                {t('dashboard.em.spaces_empty', {
                  defaultValue: 'This event has no spaces yet.',
                })}
              </p>
            ) : (
              <div className="space-y-2">
                {spaceRows.map((s) => {
                  const available = Number(s?.quantity) || 0;
                  const added = addedUnitsFor(s?.id);
                  return (
                    <div
                      key={s.id}
                      className="flex flex-wrap items-center gap-3 rounded-xl ring-1 ring-gray-200 px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {s.name}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {formatEuro(s.price)}
                          {s.size ? ` · ${s.size} m²` : ''} ·{' '}
                          {t('dashboard.em.spaces_available', {
                            defaultValue: '{{n}} available',
                            n: available,
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <label
                          htmlFor={`space-add-${s.id}`}
                          className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap"
                        >
                          {t('dashboard.em.spaces_add_units', {
                            defaultValue: 'Add units',
                          })}
                        </label>
                        <input
                          id={`space-add-${s.id}`}
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={spaceAdds[s.id] ?? ''}
                          onChange={(e) => setSpaceAdd(s.id, e.target.value)}
                          onKeyDown={blockNumberKeys}
                          disabled={!canEditAll}
                          placeholder="0"
                          className={cx(INPUT_BASE, 'w-20 text-center')}
                        />
                        <span
                          className={cx(
                            'text-[11px] font-semibold tabular-nums w-10',
                            added > 0 ? 'text-emerald-700' : 'text-transparent'
                          )}
                        >
                          → {available + added}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {newSpaces.map((s, i) => (
              <div
                key={s.key}
                className="rounded-xl ring-1 ring-amber-200 bg-brand-softer/50 p-3 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">
                    {t('dashboard.em.spaces_new_index', {
                      defaultValue: 'New space {{n}}',
                      n: i + 1,
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeNewSpace(s.key)}
                    className="p-1.5 rounded-lg hover:bg-white/70 transition"
                    aria-label={t('common.remove')}
                  >
                    <TrashIcon className="w-4 h-4 text-rose-500" />
                  </button>
                </div>

                <Field label={t('event_create.space_name')}>
                  <input
                    value={s.name}
                    onChange={(e) =>
                      updateNewSpace(s.key, 'name', e.target.value.slice(0, 255))
                    }
                    className={INPUT_CLS}
                    placeholder={t('event_create.space_name_placeholder')}
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label={t('event_create.size')}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={s.size}
                      onChange={(e) =>
                        updateNewSpace(s.key, 'size', e.target.value)
                      }
                      onKeyDown={blockNumberKeys}
                      className={INPUT_CLS}
                      placeholder={t('event_create.size_placeholder')}
                    />
                  </Field>
                  <Field label={t('event_create.available')}>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      value={s.quantity}
                      onChange={(e) =>
                        updateNewSpace(s.key, 'quantity', e.target.value)
                      }
                      onKeyDown={blockNumberKeys}
                      className={INPUT_CLS}
                      placeholder={t('event_create.available_placeholder')}
                    />
                  </Field>
                  <Field label={t('event_create.price_no_taxes')}>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500">
                        {CURRENCY_SYMBOL}
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={s.price}
                        onChange={(e) =>
                          updateNewSpace(s.key, 'price', e.target.value)
                        }
                        onKeyDown={blockNumberKeys}
                        className={cx(INPUT_CLS, 'pl-7')}
                        placeholder={t('event_create.price_placeholder')}
                      />
                    </div>
                  </Field>
                </div>

                <Field label={t('event_create.description')}>
                  <textarea
                    rows={2}
                    value={s.description}
                    onChange={(e) =>
                      updateNewSpace(
                        s.key,
                        'description',
                        e.target.value.slice(0, 255)
                      )
                    }
                    className={cx(INPUT_CLS, 'resize-none')}
                    placeholder={t('event_create.description_placeholder')}
                  />
                  <div className="text-[11px] text-gray-500 text-right mt-1">
                    {(s.description || '').length}/255
                  </div>
                </Field>
              </div>
            ))}

            {hasFloorplan && (
              <p className="text-[11px] text-amber-700">
                {t('dashboard.em.spaces_floorplan_hint', {
                  defaultValue:
                    'This event uses a floor plan — save the new capacity first, then pin it on the plan below.',
                })}
              </p>
            )}

            <p className="text-[11px] text-gray-500">
              {t('event_create.price_excl_taxes_note', {
                defaultValue: 'All prices are excluding taxes.',
              })}
            </p>
          </Section>
        </fieldset>

        {/* Section 4 — Media: its own fieldset so the regulation document
            stays editable while the event is time-locked (live/ended); the
            avatar and gallery blocks are locked individually below. */}
        <fieldset
          disabled={!canEditAll && !canEditDocs}
          className={cx(
            'space-y-5 min-w-0',
            !canEditAll && !canEditDocs && 'opacity-60 pointer-events-none'
          )}
        >
          {/* Section 4 — Media */}
          <Section
            title={t('dashboard.em.section_media_title', {
              defaultValue: 'Media',
            })}
            subtitle={t('dashboard.em.section_media_sub', {
              defaultValue:
                'Icon, regulatory document, and additional gallery images.',
            })}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Avatar */}
              <div
                className={cx(
                  'relative space-y-2',
                  !canEditAll && 'opacity-60 pointer-events-none'
                )}
              >
                {/* w-24 matches the icon tile so the label centers over it */}
                <span className="block w-24 text-center text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                  {t('event_create.icon')}
                </span>
                <input
                  key={
                    !removeExistingAvatar &&
                    (croppedImagePreview || event?.avatar)
                      ? 'has-icon'
                      : 'no-icon'
                  }
                  type="file"
                  accept={IMAGE_ACCEPT}
                  id="event-icon-upload"
                  className="sr-only"
                  disabled={!canEditAll}
                  onChange={(e) => {
                    const f = guardPickedFile(e, 'image');
                    if (!f) return;
                    setAvatarFileName(f.name);
                    setRemoveExistingAvatar(false);
                    handleAvatarPickForCrop(e);
                    e.target.value = '';
                  }}
                />
                {!removeExistingAvatar &&
                (croppedImagePreview || event?.avatar) ? (
                  <div className="relative inline-block">
                    <img
                      src={croppedImagePreview || event?.avatar}
                      alt="Event Icon"
                      className="w-24 h-24 object-cover rounded-2xl ring-1 ring-black/5"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (croppedImagePreview || croppedImage) {
                          resetAvatarCrop();
                          setAvatarFileName('');
                        } else {
                          setRemoveExistingAvatar(true);
                        }
                      }}
                      className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow ring-1 ring-black/5"
                      aria-label={t('common.remove')}
                    >
                      <XCircleIcon className="w-4 h-4 text-rose-500" />
                    </button>
                    <label
                      htmlFor="event-icon-upload"
                      className="absolute -bottom-2 left-0 right-0 mx-auto w-max text-[11px] font-semibold px-2 py-0.5 rounded-md bg-white/95 shadow ring-1 ring-black/5 cursor-pointer text-gray-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('common.change', 'Change')}
                    </label>
                  </div>
                ) : (
                  <label htmlFor="event-icon-upload" className="cursor-pointer">
                    <div className="flex items-center justify-center w-24 h-24 bg-gray-100 rounded-2xl ring-1 ring-gray-200 hover:bg-gray-200/70 transition">
                      <CameraIcon className="w-7 h-7 text-gray-400" />
                    </div>
                  </label>
                )}
              </div>

              {/* Regulatory Document */}
              <div className="space-y-2">
                <span className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                  {t('event_create.regulatory') || 'Regulatory Document'}
                </span>
                <UploadButton
                  id="event-edit-regdoc"
                  label={t('dashboard.upload_document') || 'Upload document'}
                  Icon={DocumentTextIcon}
                  accept={DOCUMENT_ACCEPT}
                  disabled={!canEditDocs}
                  onChange={(e) => {
                    const f = guardPickedFile(e, 'document');
                    if (!f) return;
                    setRegDocFile(f);
                    e.target.value = '';
                  }}
                />
                {regDocFile?.name ? (
                  <div className="relative w-full max-w-xs">
                    <div className="relative w-full rounded-lg ring-1 ring-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
                      <span className="block truncate pr-8">
                        {regDocFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setRegDocFile(null);
                        }}
                        className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow ring-1 ring-black/5"
                        aria-label={t('common.remove')}
                        disabled={!canEditDocs}
                      >
                        <XCircleIcon className="w-4 h-4 text-rose-500" />
                      </button>
                    </div>
                  </div>
                ) : event?.regulatoryDocument ? (
                  <a
                    href={event.regulatoryDocument}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-amber-800 hover:text-amber-900"
                  >
                    {t('dashboard.view_current') || 'View current'} ↗
                  </a>
                ) : (
                  <p className="text-xs text-gray-500">
                    {t('No document uploaded') || 'No document uploaded'}
                  </p>
                )}
              </div>
            </div>

            {/* Additional Images */}
            <div
              className={cx(
                'pt-4 border-t border-gray-100 space-y-3',
                !canEditAll && 'opacity-60 pointer-events-none'
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                  {t('event_create.images') || 'Additional Images'}
                </span>
                <UploadButton
                  id="event-edit-media"
                  label={t('dashboard.upload_media') || 'Upload media'}
                  Icon={PlusIcon}
                  accept={IMAGE_ACCEPT}
                  multiple
                  disabled={!canEditAll || remainingSlots === 0}
                  onChange={onPickMedia}
                  hint={`${existingMedia.length + newMedia.length}/${MAX_EVENT_MEDIA}`}
                  rightSlot={
                    remainingSlots === 0 ? (
                      <span className="text-xs text-gray-500">
                        {t('Max reached') || 'Max reached'}
                      </span>
                    ) : null
                  }
                />
              </div>

              {existingMedia.length > 0 || newMedia.length > 0 ? (
                <div className="flex gap-2 overflow-x-auto pb-1 em-nice-scroll">
                  {existingMedia.map((url, i) => (
                    <div
                      key={`existing-${i}`}
                      className="relative flex-shrink-0"
                    >
                      <img
                        src={url}
                        alt=""
                        className="w-24 h-24 object-cover rounded-xl ring-1 ring-black/5"
                      />
                      {canEditAll && (
                        <button
                          type="button"
                          onClick={() => removeExistingMedia(url)}
                          className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow ring-1 ring-black/5 hover:bg-gray-50"
                          title={t('common.remove')}
                          aria-label={t('common.remove')}
                        >
                          <XCircleIcon className="w-4 h-4 text-rose-500" />
                        </button>
                      )}
                    </div>
                  ))}
                  {newMedia.map((img) => (
                    <div key={img.id} className="relative flex-shrink-0">
                      <img
                        src={img.preview}
                        alt=""
                        className="w-24 h-24 object-cover rounded-xl ring-1 ring-black/5"
                      />
                      {canEditAll && (
                        <button
                          type="button"
                          onClick={() => removeNewMedia(img.id)}
                          className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow ring-1 ring-black/5 hover:bg-gray-50"
                          title={t('common.remove')}
                          aria-label={t('common.remove')}
                        >
                          <XCircleIcon className="w-4 h-4 text-rose-500" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  {t('No additional images yet') || 'No additional images yet'}
                </p>
              )}
            </div>
          </Section>

          {/* Section 4b — Floor plan & placements. Only shown when the event
              was created with a plan (promoters can't add one after the fact).
              Individually locked with canEditAll (like the avatar/gallery
              blocks) so the regulation document stays editable while the
              event is time-locked. */}
          {hasFloorplan && (
          <Section
            title={t('event_create.floor_plan', 'Floor plan')}
            subtitle={t('event_edit.floorplan_sub', {
              defaultValue:
                'Brands pick their spot from these pins. Pins with active bookings are locked.',
            })}
            action={
              currentFloorplanUrl && canEditAll ? (
                hasLockedPins ? (
                  <span className="text-[11px] text-gray-500">
                    {t('event_edit.floorplan_image_locked', {
                      defaultValue:
                        'Image locked — pins have active bookings',
                    })}
                  </span>
                ) : (
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="event-edit-floorplan"
                      className="text-[11px] font-bold text-gray-700 hover:underline cursor-pointer"
                    >
                      {t('event_create.replace_floor_plan', 'Replace')}
                    </label>
                    <button
                      type="button"
                      onClick={onRemoveFloorplan}
                      className="text-[11px] font-bold text-rose-600 hover:underline"
                    >
                      {t('common.remove', 'Remove')}
                    </button>
                  </div>
                )
              ) : null
            }
          >
            <div
              className={cx(
                'relative',
                !canEditAll && 'opacity-60 pointer-events-none'
              )}
            >
              <input
                id="event-edit-floorplan"
                type="file"
                accept={IMAGE_ACCEPT}
                className="sr-only"
                disabled={!canEditAll || hasLockedPins}
                onChange={onPickFloorplan}
              />
              {currentFloorplanUrl ? (
                <FloorPlan
                  key={`fp-${event?.id}-${
                    removeFloorplan
                      ? 'removed'
                      : floorplanPreview || event?.floorplanUrl || 'none'
                  }`}
                  img={currentFloorplanUrl}
                  spacesList={editorSpaces}
                  onPlacementsChange={setPlacementsDraft}
                  initialPlacements={
                    floorplanFile || removeFloorplan ? [] : initialPlacements
                  }
                  lockedPlacementIds={lockedPlacementIds}
                />
              ) : (
                <label
                  htmlFor="event-edit-floorplan"
                  className={cx(
                    'w-full py-10 rounded-2xl border border-dashed border-gray-200 bg-gray-50 grid place-items-center text-sm text-gray-500 transition',
                    canEditAll
                      ? 'cursor-pointer hover:bg-gray-100'
                      : 'cursor-not-allowed'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <CameraIcon className="w-5 h-5" />
                    {t('event_edit.floorplan_upload_cta', {
                      defaultValue: 'Upload a floor plan (PNG or JPG)',
                    })}
                  </span>
                </label>
              )}
            </div>
          </Section>
          )}
        </fieldset>

        <fieldset
          disabled={!canEditAll}
          className={cx(
            'space-y-5 min-w-0',
            !canEditAll && 'opacity-60 pointer-events-none'
          )}
        >
          {/* Section 5 — Categories */}
          <Section
            title={t('event_create.categories', { defaultValue: 'Categories' })}
            subtitle={t(
              'You can add categories; removing existing ones is disabled.'
            )}
          >
            <CategorySelectorGrid
              allowMacroSelect
              allowMicroSelect
              selectedMacros={selMacros}
              setSelectedMacros={setSelMacros}
              selectedMicros={selMicros}
              setSelectedMicros={setSelMicros}
              initialShowAllMicros
            />
          </Section>

          {/* Section 6 — Amenities */}
          <Section
            title={t('event_create.amenities')}
            subtitle={t(
              'Existing utilities are locked; you can only add new ones.'
            )}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {AMENITY_OPTIONS.map(({ label, value }) => {
                const checked = baseUtilSet.has(value) || addedUtils.has(value);
                const locked = baseUtilSet.has(value);
                return (
                  <label
                    key={value}
                    className={cx(
                      'w-full h-14 inline-flex items-center justify-center text-xs px-3 py-2 rounded-full ring-1 transition',
                      checked
                        ? 'bg-brand text-white ring-brand'
                        : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50',
                      locked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                    )}
                    title={label}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={checked}
                      onChange={() => toggleAmenity(value)}
                      disabled={locked}
                    />
                    <span className="line-clamp-2 break-words leading-tight text-center font-semibold">
                      {label}
                    </span>
                  </label>
                );
              })}
            </div>
          </Section>

          {/* Section 7 — Socials */}
          <Section title={t('event_create.social_media')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Facebook">
                <input
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                  placeholder="https://facebook.com/…"
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Instagram">
                <input
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  placeholder="https://instagram.com/…"
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="TikTok">
                <input
                  value={tiktokUrl}
                  onChange={(e) => setTiktokUrl(e.target.value)}
                  placeholder="https://tiktok.com/@…"
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="X / Twitter">
                <input
                  value={twitterUrl}
                  onChange={(e) => setTwitterUrl(e.target.value)}
                  placeholder="https://x.com/…"
                  className={INPUT_CLS}
                />
              </Field>
            </div>

            <Row label={t('Custom links')}>
              <button
                type="button"
                onClick={addCustomSocial}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200"
              >
                + {t('Add link')}
              </button>
            </Row>

            {customSocials.length === 0 ? (
              <p className="text-xs text-gray-500">{t('No custom links')}</p>
            ) : (
              <div className="space-y-2">
                {customSocials.map((row, i) => (
                  <div
                    key={`cs-${i}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2"
                  >
                    <input
                      value={row.label}
                      onChange={(e) =>
                        updateCustomSocial(i, 'label', e.target.value)
                      }
                      placeholder={t('Label (e.g., TikTok)')}
                      className={INPUT_CLS}
                    />
                    <input
                      value={row.url}
                      onChange={(e) =>
                        updateCustomSocial(i, 'url', e.target.value)
                      }
                      placeholder="https://…"
                      className={INPUT_CLS}
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomSocial(i)}
                      className="px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 ring-1 ring-gray-200"
                    >
                      {t('common.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </fieldset>

        {/* A draft's danger zone: delete it. Cancelling doesn't apply — nobody
            else has seen it, and the API refuses to cancel one. */}
        {isDraft && (
          <Section
            title={t('event_edit.delete_draft_title')}
            subtitle={t('event_edit.delete_draft_subtitle')}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xl text-xs text-gray-500">
                {t('event_edit.delete_draft_help')}
              </p>
              <button
                type="button"
                onClick={handleDeleteDraft}
                disabled={deleting}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-brick-red ring-1 ring-inset ring-red-200 bg-white hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <ClipLoader size={14} color="#a55f60" />
                ) : (
                  <TrashIcon className="w-4 h-4" />
                )}
                {t('event_edit.delete_draft_action')}
              </button>
            </div>
          </Section>
        )}

        {/* Danger zone (T2B-402). Hidden once the event is over or already in a
            terminal status — there is nothing left to call off. Also hidden on
            a draft: nobody else has seen it, and the API refuses to cancel one. */}
        {!statusLocked && !isPastNow && !isDraft && (
          <Section
            title={t('event_edit.cancel_title', 'Cancel this event')}
            subtitle={t(
              'event_edit.cancel_subtitle',
              'Only events nobody has booked yet can be cancelled here.'
            )}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xl text-xs text-gray-500">
                {canCancelEvent || bookingsLoading
                  ? t(
                      'event_edit.cancel_help',
                      'The event stops being listed and stops accepting bookings. This cannot be undone.'
                    )
                  : t('event_edit.cancel_blocked', {
                      defaultValue:
                        'This event already has {{n}} booking(s), so it can no longer be cancelled here. Contact support if you need to call it off.',
                      n: liveBookingCount,
                    })}
              </p>
              <button
                type="button"
                onClick={handleCancelEvent}
                disabled={!canCancelEvent || cancelling}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-brick-red ring-1 ring-inset ring-red-200 bg-white hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelling ? (
                  <ClipLoader size={14} color="#a55f60" />
                ) : (
                  <XCircleIcon className="w-4 h-4" />
                )}
                {t('event_edit.cancel_action', 'Cancel event')}
              </button>
            </div>
          </Section>
        )}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 -mx-1 px-1 pt-3 pb-4 mt-2 bg-gradient-to-t from-white via-white/95 to-transparent">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleDiscard}
              disabled={!canSubmit || saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('Discard', { defaultValue: 'Discard' })}
            </button>
            <button
              type="submit"
              disabled={!canSubmit || saving}
              className="inline-flex items-center justify-center px-5 py-2 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <ClipLoader size={14} color="#fff" /> {t('Saving')}
                </span>
              ) : (
                t('Save changes')
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default EventEditForm;
